/**
 * The search field at the top of a section sidebar.
 *
 * Settings had this first: a quiet row that looks like the sidebar's other
 * rows, takes focus on `/`, and swaps the sidebar's content for results while
 * there is a query. Governance and chat now wear the same field, so `/` means
 * "search this section" everywhere, and Mod+K (the strip above the inset) means
 * "search everything".
 *
 * The field owns the interaction — hotkey, arrow keys, Enter, Escape — and
 * knows nothing about what is being searched: callers pass ranked results and
 * decide what selecting one does.
 *
 * @module components/sidebar/SidebarSearch
 */
import { SearchIcon, XIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Kbd } from "../ui/kbd";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar";

export interface SidebarSearchResult {
  readonly id: string;
  readonly title: string;
  /** Second line: what kind of thing this is, or where it lives. */
  readonly detail?: string | undefined;
  readonly icon?: ReactNode;
}

/** `/` focuses the field unless the user is already typing somewhere. */
export function shouldFocusSidebarSearch(event: {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly target: EventTarget | null;
}): boolean {
  if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return false;
  const target = event.target;
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) return true;
  return !(
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable ||
    // Keep focus inside open dialogs and popups instead of escaping their
    // focus trap into the sidebar.
    target.closest('[role="dialog"], [aria-modal="true"], [data-slot$="popup"]') !== null
  );
}

export function SidebarSearch<Result extends SidebarSearchResult>({
  label,
  placeholder = "Search",
  emptyLabel,
  search,
  onSelect,
  onSearchingChange,
  onArm,
  leading,
  hotkey,
  children,
}: {
  /** Accessible name, e.g. "Search governance". */
  readonly label: string;
  /** The words in the empty field; "Search" unless the section says what it searches. */
  readonly placeholder?: string;
  readonly emptyLabel: string;
  /** Ranked results for a non-empty query. */
  readonly search: (query: string) => ReadonlyArray<Result>;
  readonly onSelect: (result: Result) => void;
  /** For content outside `children` that should also step aside for results. */
  readonly onSearchingChange?: ((searching: boolean) => void) | undefined;
  /**
   * The person is about to search: the field took focus or its hotkey fired.
   * Callers that load their results lazily start loading here, so an idle
   * sidebar costs nothing and the first keystroke already has data.
   */
  readonly onArm?: (() => void) | undefined;
  /**
   * Replaces `/` as the key that focuses the field. Chat needs this: its
   * composer holds focus and owns `/` for slash commands. A hotkey given here
   * is expected to carry modifiers, so it also fires while the person is typing.
   */
  readonly hotkey?:
    | { readonly label: string | null; readonly matches: (event: globalThis.KeyboardEvent) => boolean }
    | undefined;
  /** Rows that sit above the field, e.g. the section's primary action. */
  readonly leading?: ReactNode;
  /** The sidebar's normal content, shown while the field is empty. */
  readonly children: ReactNode;
}) {
  const { isMobile, open, setOpen, setOpenMobile } = useSidebar();
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const isSearching = query.trim().length > 0;
  const results = isSearching ? search(query) : [];
  const hasResults = results.length > 0;
  const optionId = (index: number) => `${listId}-option-${index}`;

  useEffect(() => {
    onSearchingChange?.(isSearching);
  }, [isSearching, onSearchingChange]);

  useEffect(() => {
    if (!isSearching) return;
    document.getElementById(`${listId}-option-${activeIndex}`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isSearching, listId]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (hotkey ? !hotkey.matches(event) : !shouldFocusSidebarSearch(event)) return;
      event.preventDefault();
      event.stopPropagation();
      onArm?.();
      if (isMobile) setOpenMobile(true);
      else if (!open) setOpen(true);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    };
    // Capture for a custom hotkey: ahead of the composer it has to beat.
    const capture = hotkey !== undefined;
    window.addEventListener("keydown", onKeyDown, capture);
    return () => window.removeEventListener("keydown", onKeyDown, capture);
  }, [hotkey, isMobile, onArm, open, setOpen, setOpenMobile]);

  const clear = useCallback(() => {
    setQuery("");
    setActiveIndex(0);
  }, []);

  const select = useCallback(
    (result: Result) => {
      clear();
      if (isMobile) setOpenMobile(false);
      onSelect(result);
    },
    [clear, isMobile, onSelect, setOpenMobile],
  );

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && isSearching) {
      event.preventDefault();
      event.stopPropagation();
      clear();
      return;
    }
    if (results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const result = results[activeIndex];
      if (result) select(result);
    }
  };

  return (
    <>
      {leading}
      <div
        data-sidebar-search=""
        // The same ink as the rows around it, so "Search" reads as one of them.
        className="flex h-7 shrink-0 items-center gap-[var(--sidebar-control-gap)] rounded-lg px-2 text-[13px] font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
      >
        <SearchIcon className="size-3.5 shrink-0 text-[var(--sidebar-icon-color)]" />
        <Input
          ref={inputRef}
          nativeInput
          unstyled
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setActiveIndex(0);
          }}
          onKeyDown={onInputKeyDown}
          onFocus={() => onArm?.()}
          placeholder={placeholder}
          aria-label={label}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isSearching && hasResults}
          aria-controls={isSearching && hasResults ? listId : undefined}
          aria-activedescendant={isSearching && hasResults ? optionId(activeIndex) : undefined}
          className="min-w-0 flex-1 [&_[data-slot=input]]:h-auto [&_[data-slot=input]]:p-0 [&_[data-slot=input]]:leading-normal [&_[data-slot=input]]:text-[13px] [&_[data-slot=input]]:font-medium [&_[data-slot=input]]:text-sidebar-foreground"
        />
        {isSearching ? (
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            className="size-5 shrink-0 rounded-sm text-sidebar-muted-foreground hover:bg-sidebar-control-surface hover:text-sidebar-foreground"
            aria-label="Clear search"
            onClick={() => {
              clear();
              inputRef.current?.focus();
            }}
          >
            <XIcon className="size-3" />
          </Button>
        ) : (
          (hotkey ? hotkey.label : "/") ? (
            <Kbd className="mr-px h-4 min-w-0 shrink-0 rounded-sm bg-sidebar-control-surface px-1.5 text-[10px] text-sidebar-muted-foreground ring-1 ring-sidebar-border">
              {hotkey ? hotkey.label : "/"}
            </Kbd>
          ) : null
        )}
      </div>
      {isSearching ? (
        hasResults ? (
          <SidebarMenu className="ps-px" id={listId} role="listbox" aria-label={`${label} results`}>
            {results.map((result, index) => (
              <SidebarMenuItem key={result.id} role="presentation">
                <SidebarMenuButton
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={-1}
                  size="sm"
                  isActive={index === activeIndex}
                  className="h-auto min-h-10 items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => select(result)}
                >
                  {result.icon ? <span className="mt-0.5 shrink-0">{result.icon}</span> : null}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-sidebar-foreground">
                      {result.title}
                    </span>
                    {result.detail ? (
                      <span className="block truncate text-xs font-normal text-sidebar-muted-foreground">
                        {result.detail}
                      </span>
                    ) : null}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        ) : (
          <p role="status" className="px-2 py-6 text-center text-xs text-sidebar-muted-foreground">
            {emptyLabel}
          </p>
        )
      ) : (
        children
      )}
    </>
  );
}
